import { Breadcrumbs, Loader } from '@mantine/core';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '@xver/hooks/useAppDispatch';
import useBreadcrumbItems from '@xver/hooks/useBreadcrumbItems';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useMeQuery } from '@xver/hooks/useMeQuery';
import { ApplicationState } from '@xver/store';
import { setAuthUser } from '@xver/store/auth/auth.actions';

import GitHubConnectionSection from './components/GitHubConnectionSection';
import KYCSection from './components/KYCSection';
import ProfileEditForm from './components/ProfileEditForm';
import ProfileSummaryCard from './components/ProfileSummaryCard';
import TrialCreditSection from './components/TrialCreditSection';
import { useUpdateProfileMutation } from './hooks/useUpdateProfileMutation';

export default function ProfilePage() {
    const { t } = useTranslation('profile');
    const headingRef = usePageHeading(t('page.title'));
    const dispatch = useAppDispatch();
    const cachedUser = useSelector((state: ApplicationState) => state.auth.user);
    const { data: freshUser, isPending } = useMeQuery();
    const mutation = useUpdateProfileMutation();

    useEffect(() => {
        if (freshUser) dispatch(setAuthUser(freshUser));
    }, [freshUser, dispatch]);

    const user = freshUser ?? cachedUser;

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.account'), href: '#' },
        { title: t('breadcrumb.profile') },
    ]);

    return (
        <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
            <div className="w-full flex flex-col justify-start items-start">
                <Breadcrumbs separatorMargin="xs" className="mb-6">
                    {breadcrumbItems}
                </Breadcrumbs>
                <div className="w-full flex flex-col gap-1">
                    <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                        {t('page.title')}
                    </h1>
                    <p className="w-auto max-w-135 text-base text-fg/68">{t('page.description')}</p>
                </div>
            </div>
            <div className="w-full flex flex-col gap-4">
                {!user || (isPending && !cachedUser) ? (
                    <div className="w-full flex items-center justify-center py-16">
                        <Loader size="md" type="bars" />
                    </div>
                ) : (
                    <>
                        <ProfileSummaryCard user={user} />
                        <TrialCreditSection />
                        <GitHubConnectionSection user={user} />
                        <ProfileEditForm
                            user={user}
                            onSubmit={(payload) => mutation.mutateAsync(payload)}
                            isSubmitting={mutation.isPending}
                        />
                        <KYCSection user={user} />
                    </>
                )}
            </div>
        </div>
    );
}
